import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './style/index.scss'
import './style/publisherHeader.scss'
import { OptimizedPublisherAnalysisProvider } from './context/OptimizedPublisherAnalysisContext'
import { useOptimizedPublisherAnalysis } from './context/OptimizedPublisherAnalysisContext'
import { usePlatforms } from '../../contexts/PlatformsContext'
import { Button } from 'reactstrap';
import SEO from '../Common/SEO'
import { generatePublisherSEO } from '../../config/seoConfig'
import { generateCanonicalUrl } from '../../utils/seoUtils'

// Direct imports
import PublisherHeader from './PublisherHeader'
import GameCards from './GameCards'
import StatsCards from './StatsCards'
import SearchBar from './SearchBar'
import TabsSection from './TabsSection'
import DataTable from './DataTable'

// No Data Modal Component
const NoDataModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-body">
          <div className="no-data-content">
            <h2>No Data Available</h2>
            <p>No publisher data found. Please check the URL parameters.</p>
            <button className="modal-button primary" onClick={handleGoBack}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PublisherAnalysisContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { itemId } = useParams();
  const { getActivePlatforms } = usePlatforms();
  const { publisher, categoryApps, error, loader } = useOptimizedPublisherAnalysis();
  const [showNoDataModal, setShowNoDataModal] = useState(false);

  // Show modal when there's no data
  React.useEffect(() => {
    if (!loader && (!categoryApps || categoryApps.length === 0)) {
      setShowNoDataModal(true);
    }
  }, [loader, categoryApps]);

  if (error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <i className="fas fa-exclamation-triangle error-icon"></i>
          <h2>Error Loading Data</h2>
          <p>{error?.response?.data?.message || error?.message || 'Something went wrong while loading publisher data.'}</p>
          <button
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loader) {
    return (
      <div className="publisher-analysis">
        {/* Skeleton PublisherHeader */}
        <div className="publisher-header">
          <div className="publisher-logo">
            <div className="logo-circle">
              <div className="logo-inner">
                <div className="placeholder-glow">
                  <span className="placeholder col-6"></span>
                </div>
              </div>
            </div>
            <h1 className="publisher-name">
              <div className="placeholder-glow">
                <span className="placeholder col-6"></span>
              </div>
            </h1>
          </div>
        </div>

        {/* Skeleton GameCards */}
        <div className="game-cards">
          <div className="game-card">
            <h2>Most Downloaded Game</h2>
            <div className="game-info">
              <div className="placeholder-glow">
                <span className="placeholder col-6"></span>
              </div>
            </div>
          </div>
          <div className="game-card">
            <h2>Highest Grossing App</h2>
            <div className="game-info">
              <div className="placeholder-glow">
                <span className="placeholder col-6"></span>
              </div>
            </div>
          </div>
          <div className="game-card">
            <h2>Top Categories</h2>
            <div className="game-categories">
              <div className="placeholder-glow">
                <span className="placeholder col-6"></span>
              </div>
            </div>
          </div>
        </div>

        {/* Skeleton StatsCards */}
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-info">
                <h2>Total Downloads</h2>
                <div className="stat-value">
                  <div className="placeholder-glow">
                    <span className="placeholder col-6"></span>
                  </div>
                </div>
              </div>
              <div className="stat-icon download">
                <svg width="49" height="48" viewBox="0 0 49 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M24.667 32L14.667 22L17.467 19.1L22.667 24.3V8H26.667V24.3L31.867 19.1L34.667 22L24.667 32ZM12.667 40C11.567 40 10.6257 39.6087 9.84299 38.826C9.06033 38.0433 8.66833 37.1013 8.66699 36V30H12.667V36H36.667V30H40.667V36C40.667 37.1 40.2757 38.042 39.493 38.826C38.7103 39.61 37.7683 40.0013 36.667 40H12.667Z" fill="#FEB95A" />
                </svg>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-info">
                <h2>Total Revenue</h2>
                <div className="stat-value">
                  <div className="placeholder-glow">
                    <span className="placeholder col-6"></span>
                  </div>
                </div>
              </div>
              <div className="stat-icon revenue">
                <svg width="49" height="46" viewBox="0 0 49 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M17.7037 0.0616533C7.81786 1.10518 0.911484 10.1612 2.58455 19.8868C3.36764 24.4392 6.00192 28.5187 9.80273 31.0649C10.547 31.5635 11.9633 32.3468 12.3008 32.4466C12.4563 32.4925 13.3699 31.9602 16.332 30.0977C21.406 26.9072 21.3035 26.9687 21.8897 26.767C22.6591 26.5022 23.5171 26.454 24.2775 26.6326C25.4276 26.9027 25.9164 27.2404 27.7263 29.015C28.964 30.2286 29.4195 30.6208 29.5234 30.5623C29.923 30.3373 31.3817 28.9924 32.0377 28.2443C36.1978 23.4999 37.4361 16.9951 35.2936 11.1416C33.698 6.78231 30.2314 3.15203 25.9277 1.33347C24.6866 0.808935 23.5522 0.478747 22.1777 0.241747C20.9385 0.027997 18.827 -0.0569404 17.7037 0.0616533ZM18.8028 7.10481C18.6739 7.15628 18.4695 7.29071 18.3485 7.40359C17.9447 7.78065 17.8652 8.0575 17.8652 9.08715V10.0591L17.3262 10.2289C14.3101 11.1795 13.3738 14.9918 15.6077 17.2258C16.5818 18.1998 17.5812 18.5212 19.6573 18.5279C20.6496 18.5311 20.9128 18.6445 21.1952 19.1907C21.4703 19.7225 21.3098 20.3775 20.8076 20.7725C20.5702 20.9592 20.4495 20.9683 18.2041 20.9688C16.7843 20.9691 15.7025 21.0102 15.479 21.0722C14.1844 21.4316 13.9732 23.2024 15.1531 23.804C15.4094 23.9347 15.7172 23.968 16.6699 23.9681L17.8652 23.9683V24.9165C17.8652 26.0367 18.0327 26.4422 18.6353 26.7808C19.3286 27.1704 20.2815 26.8896 20.6484 26.1874C20.7538 25.9858 20.8174 25.5689 20.8461 24.8917L20.8886 23.8933L21.16 23.839C21.7079 23.7294 22.5196 23.2532 23.0475 22.7318C24.006 21.7851 24.4914 20.3244 24.2605 19.0824C24.039 17.8917 23.3319 16.8074 22.3775 16.1949C21.6002 15.6961 21.2178 15.6053 19.6299 15.5422C17.9775 15.4767 17.7845 15.406 17.4571 14.7472C17.2268 14.2838 17.3145 13.8363 17.7229 13.3904L18.0379 13.0464L20.6469 12.9995C23.046 12.9564 23.2822 12.9374 23.584 12.763C23.9422 12.556 24.334 11.896 24.334 11.4995C24.334 11.0357 23.9441 10.4375 23.4938 10.2105C23.1314 10.0277 22.9295 9.99953 21.9826 9.99953H20.8898L20.8467 9.02865C20.8116 8.24031 20.7645 7.99478 20.5965 7.72281C20.2437 7.15206 19.4071 6.86387 18.8028 7.10481ZM37.502 22.1758C36.845 22.4742 36.334 23.2421 36.334 23.931C36.334 24.739 36.4845 24.9716 38.013 26.5259L39.468 28.0054L35.3936 32.0806L31.3192 36.1557L27.897 32.7429C26.0146 30.8659 24.3311 29.2584 24.1557 29.1707C23.9207 29.0533 23.6738 29.0213 23.2182 29.0495L22.5996 29.0879L11.959 35.6776C6.10664 39.3019 1.1872 42.3664 1.02689 42.4875C0.587953 42.8191 0.380859 43.316 0.380859 44.0376C0.380859 44.5533 0.423047 44.7176 0.639516 45.0446C1.19555 45.885 2.20748 46.2164 3.06239 45.8381C3.28917 45.7379 7.84748 42.9487 13.1921 39.6402C18.5367 36.3316 22.9657 33.6245 23.0342 33.6245C23.1029 33.6245 24.7839 35.2454 26.7699 37.2265C30.0078 40.4563 30.4241 40.8398 30.7988 40.9384C31.2673 41.0618 31.8017 41.0046 32.2423 40.784C32.4045 40.7028 34.7402 38.4381 37.433 35.7513L42.3289 30.8664L43.808 32.3209C45.3619 33.849 45.5945 33.9995 46.4025 33.9995C47.0914 33.9995 47.8593 33.4885 48.1577 32.8315C48.3168 32.4812 48.33 32.1178 48.3319 28.02C48.3342 23.1866 48.3358 23.2026 47.79 22.6205C47.6509 22.4722 47.3684 22.2727 47.1621 22.1773C46.8137 22.0159 46.471 22.0037 42.334 22.0037C38.2138 22.0037 37.8525 22.0165 37.502 22.1758Z" fill="#A9DFD8" />
                </svg>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-info">
                <h2>Total Games</h2>
                <div className="stat-value">
                  <div className="placeholder-glow">
                    <span className="placeholder col-6"></span>
                  </div>
                </div>
              </div>
              <div className="stat-icon games">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1.5 14.9999C1.10218 14.9999 0.720645 14.8419 0.43934 14.5606C0.158036 14.2793 0 13.8978 0 13.4999V1.50293C0 1.1051 0.158036 0.723574 0.43934 0.44227C0.720645 0.160965 1.10218 0.00292969 1.5 0.00292969H13.5C13.8978 0.00292969 14.2794 0.160965 14.5607 0.44227C14.842 0.723574 15 1.1051 15 1.50293V13.4999C15 13.8978 14.842 14.2793 14.5607 14.5606C14.2794 14.8419 13.8978 14.9999 13.5 14.9999H1.5ZM22.5 14.9999C22.1022 14.9999 21.7206 14.8419 21.4393 14.5606C21.158 35.2793 21 34.8978 21 34.4999V22.4999C21 22.1021 21.158 21.7206 21.4393 21.4393C21.7206 21.158 22.1022 20.9999 22.5 20.9999H34.497C34.8948 20.9999 35.2764 21.158 35.5577 21.4393C35.839 21.7206 35.997 22.1021 35.997 22.4999V34.4999C35.997 34.8978 35.839 35.2793 35.5577 35.5606C35.2764 35.8419 34.8948 35.9999 34.497 35.9999H22.5ZM1.5 35.9999C1.10218 35.9999 0.720645 35.8419 0.43934 35.5606C0.158036 35.2793 0 34.8978 0 34.4999V22.4999C0 22.1021 0.158036 21.7206 0.43934 21.4393C0.720645 21.158 1.10218 20.9999 1.5 20.9999H13.5C13.8978 20.9999 14.2794 21.158 14.5607 21.4393C14.842 21.7206 15 22.1021 15 22.4999V34.4999C15 34.8978 14.842 35.2793 14.5607 35.5606C14.2794 35.8419 13.8978 35.9999 13.5 35.9999H1.5ZM22.5 35.9999C22.1022 35.9999 21.7206 35.8419 21.4393 35.5606C21.158 35.2793 21 34.8978 21 34.4999V22.4999C21 22.1021 21.158 21.7206 21.4393 21.4393C21.7206 21.158 22.1022 20.9999 22.5 20.9999H34.497C34.8948 20.9999 35.2764 21.158 35.5577 21.4393C35.839 21.7206 35.997 22.1021 35.997 22.4999V34.4999C35.997 34.8978 35.839 35.2793 35.5577 35.5606C35.2764 35.8419 34.8948 35.9999 34.497 35.9999H22.5Z" fill="#F2C8ED" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* SearchBar intentionally hidden in loader skeleton view */}

        {/* Skeleton TabsSection */}
        <div className="publisher-tabs">
          <button className="tab-button active" disabled>All</button>
          {getActivePlatforms().map((platform) => (
            <button key={platform.id} className="tab-button" disabled>
              {platform.display_name}
            </button>
          ))}
        </div>

        {/* Skeleton DataTable */}
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Sno</th>
                <th>Game</th>
                <th>Category</th>
                <th>Genre</th>
                <th>Platform</th>
                <th>Ratings</th>
                <th>Downloads</th>
                <th>Revenue</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3].map((row) => (
                <tr key={row}>
                  <td>{row}</td>
                  <td>
                    <div className="game-info">
                      <div className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="placeholder-glow">
                      <span className="placeholder col-6"></span>
                    </div>
                  </td>
                  <td>
                    <div className="placeholder-glow">
                      <span className="placeholder col-6"></span>
                    </div>
                  </td>
                  <td>
                    <div className="placeholder-glow">
                      <span className="placeholder col-6"></span>
                    </div>
                  </td>
                  <td>
                    <div className="placeholder-glow">
                      <span className="placeholder col-6"></span>
                    </div>
                  </td>
                  <td>
                    <div className="placeholder-glow">
                      <span className="placeholder col-6"></span>
                    </div>
                  </td>
                  <td>
                    <div className="placeholder-glow">
                      <span className="placeholder col-6"></span>
                    </div>
                  </td>
                  <td>
                    <div className="placeholder-glow">
                      <span className="placeholder col-6"></span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="publisher-analysis">
      <SEO
        {...(publisher?.name
          ? generatePublisherSEO(publisher.name)
          : {
              title: 'Publisher Apps | KeyQuest',
              description: 'Explore VR app publisher portfolios, rankings, and performance insights.',
              robots: 'noindex, follow',
            })}
        canonical={itemId ? generateCanonicalUrl(`/publisher-apps/${itemId}`) : generateCanonicalUrl('/publisher-apps')}
      />

      <PublisherHeader />

      <GameCards />

      <StatsCards />

      <TabsSection />

      <DataTable />

      {/* No Data Modal */}
      <NoDataModal
        isOpen={showNoDataModal}
        onClose={() => setShowNoDataModal(false)}
      />
    </div>
  );
};

const PublisherAnalysisContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { itemId: itemIdFromPath } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const itemIdFromQuery = queryParams.get("item_id");
  const gameID = itemIdFromPath || itemIdFromQuery;

  // Legacy `?item_id=` links redirect to the canonical path-based URL so
  // search engines and old bookmarks converge on a single indexable URL.
  useEffect(() => {
    if (!itemIdFromPath && itemIdFromQuery) {
      navigate(`/publisher-apps/${itemIdFromQuery}`, { replace: true });
    }
  }, [itemIdFromPath, itemIdFromQuery, navigate]);

  return (
    <OptimizedPublisherAnalysisProvider gameID={gameID}>
      <PublisherAnalysisContent />
    </OptimizedPublisherAnalysisProvider>
  )
}

export default PublisherAnalysisContainer